import React, { useState, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { JobArtifact } from '@kaizen/shared';
import {
  FileText,
  Download,
  Copy,
  Check,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  Code2,
  WifiOff,
  Laptop
} from 'lucide-react';
import { formatBytes, formatDate } from '../../lib/utils';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import {
  kaizenDialogTransition,
  kaizenOverlayTransition
} from '../../lib/kaizenMotion';

interface FileViewerModalProps {
  artifact: JobArtifact | null;
  isOpen: boolean;
  onClose: () => void;
  onSendToAssistant?: (artifact: JobArtifact) => void;
}

export const FileViewerModal: React.FC<FileViewerModalProps> = ({
  artifact,
  isOpen,
  onClose,
  onSendToAssistant
}) => {
  const { displayPath } = usePrivacy();
  const { getArtifactContent, deviceStatus } = useRealtime();

  const [loading, setLoading] = useState<boolean>(false);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFallbackPreview, setIsFallbackPreview] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [formatJson, setFormatJson] = useState<boolean>(false);
  const [wrapLines, setWrapLines] = useState<boolean>(true);

  const isJson = useMemo(() => {
    if (!artifact) return false;
    return (
      artifact.mimeType === 'application/json' ||
      artifact.filename.toLowerCase().endsWith('.json')
    );
  }, [artifact]);

  const fetchContent = async (art: JobArtifact) => {
    setLoading(true);
    setError(null);
    setIsFallbackPreview(false);
    setContent(null);

    if (deviceStatus !== 'online') {
      if (art.contentPreview) {
        setContent(art.contentPreview);
        setIsFallbackPreview(true);
        setError('KAIZEN Agent is offline. Showing cached preview (first 4KB). Connect agent to retrieve full content.');
      } else {
        setError('KAIZEN Agent is offline. Cannot retrieve artifact content.');
      }
      setLoading(false);
      return;
    }

    try {
      const result = await getArtifactContent(art.id);
      if (result.success && typeof result.content === 'string') {
        setContent(result.content);
      } else {
        const errorMsg = result.error || 'Failed to retrieve artifact content from agent.';
        setError(errorMsg);
        if (art.contentPreview) {
          setContent(art.contentPreview);
          setIsFallbackPreview(true);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error communicating with agent';
      setError(msg);
      if (art.contentPreview) {
        setContent(art.contentPreview);
        setIsFallbackPreview(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && artifact) {
      void fetchContent(artifact);
    } else {
      setContent(null);
      setError(null);
      setLoading(false);
      setCopied(false);
      setFormatJson(false);
      setIsFallbackPreview(false);
    }
  }, [isOpen, artifact?.id]);

  const displayedContent = useMemo(() => {
    if (!content) return '';
    if (isJson && formatJson) {
      try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return content;
      }
    }
    return content;
  }, [content, isJson, formatJson]);

  const handleCopy = () => {
    const textToCopy = content || artifact?.contentPreview || '';
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const textToDownload = content || artifact?.contentPreview || '';
    if (!textToDownload || !artifact) return;
    const mime = artifact.mimeType || 'text/plain;charset=utf-8';
    const blob = new Blob([textToDownload], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!artifact) return null;

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={kaizenOverlayTransition}
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 8,
              scale: 0.985
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }}
            exit={{
              opacity: 0,
              y: 6,
              scale: 0.99
            }}
            transition={kaizenDialogTransition}
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-3xl w-full flex flex-col max-h-[90vh] overflow-hidden"
          >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
              <FileText className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h3 className="font-mono font-semibold text-xs sm:text-sm text-slate-800 truncate" title={artifact.filename}>
                {artifact.filename}
              </h3>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                <span>{formatBytes(artifact.size)}</span>
                <span>â€¢</span>
                <span className="truncate">{artifact.mimeType}</span>
                <span>â€¢</span>
                <span>{formatDate(artifact.createdAt)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-2">
            <button
              onClick={() => void fetchContent(artifact)}
              disabled={loading}
              title="Refresh artifact content"
              className="p-2 sm:p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 flex items-center justify-center disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              title="Close modal"
              className="p-2 sm:p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Local Path & Agent Info Bar */}
        <div className="px-4 sm:px-5 py-2 bg-slate-100 border-b border-slate-200 text-[11px] font-mono text-slate-600 flex items-center justify-between gap-2 overflow-hidden">
          <span className="truncate">
            <span className="text-slate-400">Path: </span>
            {displayPath(artifact.localPath)}
          </span>
          <div className="flex items-center gap-1.5 shrink-0 text-[10px]">
            {deviceStatus === 'online' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>Agent Online</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <WifiOff className="w-3 h-3 text-amber-500" />
                <span>Agent Offline</span>
              </span>
            )}
          </div>
        </div>

        {/* Error / Warning Alert */}
        {error && (
          <div className={`px-4 sm:px-5 py-2.5 border-b text-xs flex items-start gap-2.5 ${
            isFallbackPreview
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}>
            <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${
              isFallbackPreview ? 'text-amber-600' : 'text-rose-600'
            }`} />
            <div className="flex-1 text-[11px] leading-relaxed">
              <span className="font-semibold">{error}</span>
              {isFallbackPreview && (
                <span className="block text-[10px] text-amber-700 mt-0.5">
                  Showing initial preview captured during job completion.
                </span>
              )}
            </div>
          </div>
        )}

        {/* Toolbar Controls */}
        <div className="px-4 sm:px-5 py-2 bg-slate-900 border-b border-slate-800 flex items-center justify-between text-xs text-slate-300">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400 font-mono">
              {content ? `${formatBytes(content.length)} loaded` : 'â€”'}
            </span>
            {isFallbackPreview && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-900/60 text-amber-300 border border-amber-700/60 font-mono">
                Preview Only
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isJson && (
              <button
                onClick={() => setFormatJson(!formatJson)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono transition-colors ${
                  formatJson
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <Code2 className="w-3 h-3" />
                <span>{formatJson ? 'Raw JSON' : 'Format JSON'}</span>
              </button>
            )}
            <button
              onClick={() => setWrapLines(!wrapLines)}
              className="px-2 py-1 rounded text-[11px] font-mono bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
            >
              {wrapLines ? 'No Wrap' : 'Wrap'}
            </button>
          </div>
        </div>

        {/* Content Viewer Body */}
        <div className="flex-1 overflow-y-auto overflow-x-auto bg-slate-950 text-slate-200 font-mono text-xs leading-relaxed selection:bg-indigo-600 p-4 min-h-[220px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              <div className="text-center">
                <p className="text-sm font-medium text-slate-200">Retrieving Artifact Content</p>
                <p className="text-xs text-slate-400 mt-1">Fetching full text securely from Windows Agent...</p>
              </div>
            </div>
          ) : displayedContent ? (
            <pre className={`${wrapLines ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'} selection:bg-indigo-700 selection:text-white`}>
              {displayedContent}
            </pre>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <FileText className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-xs">{error || 'No content available for this artifact.'}</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 px-4 sm:px-5 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center gap-2">
            {onSendToAssistant && (
              <button
                onClick={() => {
                  onSendToAssistant(artifact);
                  onClose();
                }}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl border border-indigo-200 transition-colors min-h-[44px] sm:min-h-0"
              >
                <Laptop className="w-3.5 h-3.5" />
                <span>Send to JARVIS</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={loading || !content}
              className={`flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-xl border transition-all min-h-[44px] sm:min-h-0 active:scale-95 disabled:opacity-40 disabled:pointer-events-none ${
                copied
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100 shadow-sm'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="font-semibold">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copy Text</span>
                </>
              )}
            </button>

            <button
              onClick={handleDownload}
              disabled={loading || !content}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-all min-h-[44px] sm:min-h-0 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Download</span>
            </button>
          </div>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
